import { fetchPlanBehaviorAnalyze } from '@/apis';
import { translateToEn, translation } from '../plugins/i18n';

export const PLAN_BEHAVIOR_ANALYZE_KEY = 'fbc-plan-behavior-analyze-data';
export const PLAN_BEHAVIOR_ANALYZE_TIMESTAMP_KEY = 'fbc-plan-behavior-analyze-timestamp';
export const CACHE_EXPIRY_TIME = 2 * 60 * 60 * 1000; // 2 hours

// Create a reactive store structure
let planBehaviorAnalyzeStore = {
  state: {
    results: []
  },
  errorCode: null,
  update(newState) {
    this.state = newState;
    this.errorCode = null;
  },
  setError(errorCode) {
    this.errorCode = errorCode;
  }
};

export { planBehaviorAnalyzeStore };

const isValidData = (data) => {
  return data && Array.isArray(data) && data.length > 0;
};

const isCacheExpired = () => {
  try {
    const timestamp = sessionStorage.getItem(PLAN_BEHAVIOR_ANALYZE_TIMESTAMP_KEY);
    if (!timestamp) {
      return true;
    }
    const now = Date.now();
    const cachedTime = parseInt(timestamp, 10);
    return now - cachedTime > CACHE_EXPIRY_TIME;
  } catch (error) {
    console.error('PLAN_BEHAVIOR_ANALYZE - Check cache expiry error:', error);
    return true;
  }
};

const setCacheData = (data) => {
  try {
    sessionStorage.setItem(PLAN_BEHAVIOR_ANALYZE_KEY, JSON.stringify(data));
    sessionStorage.setItem(PLAN_BEHAVIOR_ANALYZE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('PLAN_BEHAVIOR_ANALYZE - Set cache error:', error);
  }
};

/**
 * Process plan behavior data and add buttonProps based on priority
 * @param {Array} data - Raw API response data
 * @returns {Array} Processed data with buttonProps
 */
const processPlanBehaviorData = (data) => {
  if (!Array.isArray(data)) {
    return data;
  }
  const fromMaps = {
    'ZVU': 'pbx_ZVU',
    'ZONEPROPZVU': 'zoneproplus_zonepropzvu',
    'ZONEBIZPZVU': 'zonebundle_zonebizpzvu',
  };

  return data.map(item => {
    const processedItem = { ...item };
    let transKey='';
    // Priority order: canUpgradeTo > canEdit > canAdd2Cart > canDowngradeTo
    if (item.canUpgradeTo === true) {
      transKey='fbc.common.button.upgrade';
      processedItem.buttonProps = {
        btnDisabled: false,
        planLink: item.redirectUrl,
        freeTrialLink: item.redirectUrl,
      };
    } else if (item.canEdit === true) {
      transKey='fbc.common.button.add_license';
      processedItem.buttonProps = {
        btnDisabled: false,
        planLink: `/opc/buy/add?type=upsell&from=${fromMaps[item.bizCategoryCode]}`,
        freeTrialLink: item.redirectUrl,
      };
    } else if (item.canAdd2Cart === true) {
       transKey = item.bizCategoryCode === 'ZVU' ? 'fbc.common.button.buy_now_lower' : 'fbc.common.button.upgrade';
      processedItem.buttonProps = {
        btnDisabled: false,
        planLink: `/opc/buy/add?type=upsell&from=${fromMaps[item.bizCategoryCode]}`,
        freeTrialLink: item.redirectUrl,
      };
    } else if (item.canDowngradeTo === true) {
      transKey='fbc.common.button.downgrade';
      processedItem.buttonProps = {
        btnDisabled: true,
        planLink: ''
      };
    } else {
      transKey = 'fbc.common.button.buy_now_lower';
      processedItem.buttonProps = {
        btnDisabled: true,
        planLink: '',
      };
    }
    processedItem.buttonProps.btnLabel = translation(transKey);
    processedItem.buttonProps.btnLabelEn = translateToEn(transKey);
    return processedItem;
  });
};

/**
 * Get plan behavior analyze data
 * @param {Array} bizCategoryCodeList - Array of business category codes
 * @param {boolean} useCacheSessionStorage - Whether to use cache, default true
 */
export const getPlanBehaviorAnalyze = async (bizCategoryCodeList = [], useCacheSessionStorage = false) => {
  if (!useCacheSessionStorage) {
    try {
      let result = await fetchPlanBehaviorAnalyze(bizCategoryCodeList);
      if (isValidData(result)) {

        const processedResult = processPlanBehaviorData(result);
        setCacheData(processedResult);
        planBehaviorAnalyzeStore.update({ results: processedResult });
        return processedResult;
      }

      return result;
    } catch (error) {
      console.error('PLAN_BEHAVIOR_ANALYZE - API error:', error);
      planBehaviorAnalyzeStore.setError(error.response?.status || error.code || 500);
      throw error;
    }
  }

  // Create cache key based on bizCategoryCodeList
  const cacheKey = `${PLAN_BEHAVIOR_ANALYZE_KEY}_${bizCategoryCodeList.sort().join(',')}`;

  if (planBehaviorAnalyzeStore && isValidData(planBehaviorAnalyzeStore.state.results) && !isCacheExpired()) {
    return planBehaviorAnalyzeStore.state.results;
  }

  try {
    const storedValue = sessionStorage.getItem(cacheKey);
    if (storedValue && !isCacheExpired()) {
      const cachedData = JSON.parse(storedValue);
      if (isValidData(cachedData)) {
        planBehaviorAnalyzeStore.update({ results: cachedData });
        return cachedData;
      }
    }
  } catch (error) {
    sessionStorage.removeItem(cacheKey);
    sessionStorage.removeItem(PLAN_BEHAVIOR_ANALYZE_TIMESTAMP_KEY);
    console.error('PLAN_BEHAVIOR_ANALYZE - Cache error:', error);
  }

  try {
    let result = await fetchPlanBehaviorAnalyze(bizCategoryCodeList);
    /*result = [
      {
        'bizCategoryCode': 'ZVU',
        'canAdd2Cart': true,
        'canEdit': false,
        'canUpgradeTo': false,
        'canDowngradeTo': false,
      }, {
        'bizCategoryCode': 'ZONEPROPZVU',
        'canAdd2Cart': false,
        'canEdit': true,
        'canUpgradeTo': false,
        'canDowngradeTo': false,
      },
      {
        'bizCategoryCode': 'ZONEBIZPZVU',
        'canAdd2Cart': false,
        'canEdit': true,
        'canUpgradeTo': true,
        'canDowngradeTo': false,
      },
    ];*/

    if (isValidData(result)) {
      const processedResult = processPlanBehaviorData(result);
      setCacheData(processedResult);
      planBehaviorAnalyzeStore.update({ results: processedResult });
      return processedResult;
    }

    return result;
  } catch (error) {
    console.error('PLAN_BEHAVIOR_ANALYZE - API error:', error);
    planBehaviorAnalyzeStore.setError(error.response?.status || error.code || 500);
    throw error;
  }
};

/**
 * Clear cache
 */
export const clearPlanBehaviorAnalyzeCache = () => {
  planBehaviorAnalyzeStore.update({ results: [] });
  // Clear all cache entries for this API
  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith(PLAN_BEHAVIOR_ANALYZE_KEY)) {
      sessionStorage.removeItem(key);
    }
  });
  sessionStorage.removeItem(PLAN_BEHAVIOR_ANALYZE_TIMESTAMP_KEY);
};
